import { useState } from 'react';

import Dialog from '@/components/ui/Dialog';
import {
  type ArticleCategoryScope,
  createArticleCategoryApi,
  deleteArticleCategoryApi,
  getArticleCategoriesApi,
  updateArticleCategoryApi,
} from '@/lib/api/blogApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

// `toCategoryScope` vit dans ./category-scope.ts : react-refresh interdit
// d'exporter autre chose qu'un composant depuis ce fichier.

interface CategoryManagerProps {
  open: boolean;
  onClose: () => void;
  /**
   * Blog dont on gere la taxonomie (Lot A bis).
   * Absent = referentiel editorial BoulesConnect (SUPER_ADMIN).
   * Renseigne = categories privees du club / de l'organisation.
   */
  scope?: ArticleCategoryScope;
}

export default function CategoryManager({ open, onClose, scope }: CategoryManagerProps) {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const { data: categories, isLoading } = useQuery({
    queryKey: queryKeys.blog.categories(scope),
    queryFn: () => getArticleCategoriesApi(scope),
    enabled: isAuthenticated && open,
  });

  // Une seule cle d'invalidation, calculee une fois : sans le scope, la
  // taxonomie d'un club et celle de la plateforme partageraient la meme entree
  // de cache et se contamineraient d'un ecran a l'autre.
  const categoriesKey = queryKeys.blog.categories(scope);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3B82F6');

  function resetForm() {
    setEditingId(null);
    setName('');
    setColor('#3B82F6');
  }

  function startEdit(cat: { id: number; name: string; color: string | null }) {
    setEditingId(cat.id);
    setName(cat.name);
    setColor(cat.color ?? '#3B82F6');
  }

  const createMutation = useMutation({
    mutationFn: () => createArticleCategoryApi({ name: name.trim(), color }, scope),
    onSuccess: () => {
      toast.success('Categorie creee');
      void queryClient.invalidateQueries({ queryKey: categoriesKey });
      resetForm();
    },
    onError: () => toast.error('Erreur lors de la creation'),
  });

  const updateMutation = useMutation({
    mutationFn: () => updateArticleCategoryApi(editingId!, { name: name.trim(), color }, scope),
    onSuccess: () => {
      toast.success('Categorie modifiee');
      void queryClient.invalidateQueries({ queryKey: categoriesKey });
      resetForm();
    },
    onError: () => toast.error('Erreur lors de la modification'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteArticleCategoryApi(id, scope),
    onSuccess: () => {
      toast.success('Categorie supprimee');
      void queryClient.invalidateQueries({ queryKey: categoriesKey });
    },
    // Le serveur refuse desormais explicitement la suppression d'une categorie
    // encore utilisee (409) plutot que de remonter une erreur de cle etrangere.
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Impossible de supprimer (articles lies ?)'),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    if (editingId) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  }

  function handleDelete(id: number, catName: string) {
    if (!window.confirm(`Supprimer la categorie "${catName}" ?`)) return;
    deleteMutation.mutate(id);
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} title='Gerer les categories' className='max-w-md'>
      <div className='p-6'>
        <div className='mb-6 flex items-center justify-between'>
          <h2 className='text-lg font-bold text-slate-900'>Categories d&apos;articles</h2>
          <button onClick={onClose} className='rounded-lg p-1.5 hover:bg-slate-100 transition'>
            <X className='h-5 w-5 text-slate-500' />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className='mb-6 flex items-end gap-2'>
          <div className='flex-1'>
            <label className='mb-1 block text-xs font-semibold text-slate-600'>Nom</label>
            <input
              type='text'
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder='Ex: Tutoriels'
              className='w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none'
            />
          </div>
          <div>
            <label className='mb-1 block text-xs font-semibold text-slate-600'>Couleur</label>
            <input
              type='color'
              value={color}
              onChange={e => setColor(e.target.value)}
              className='h-[38px] w-12 cursor-pointer rounded-lg border border-slate-200 p-0.5'
            />
          </div>
          <button
            type='submit'
            disabled={isPending}
            className='inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50'
          >
            {editingId ? (
              'Modifier'
            ) : (
              <>
                <Plus className='h-3.5 w-3.5' /> Ajouter
              </>
            )}
          </button>
          {editingId && (
            <button
              type='button'
              onClick={resetForm}
              className='rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50'
            >
              Annuler
            </button>
          )}
        </form>

        {/* List */}
        {isLoading ? (
          <div className='flex justify-center py-8'>
            <div className='h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent' />
          </div>
        ) : (categories ?? []).length === 0 ? (
          <p className='py-8 text-center text-sm text-slate-500'>Aucune categorie</p>
        ) : (
          <ul className='divide-y divide-slate-100 rounded-xl border border-slate-200'>
            {(categories ?? []).map(cat => (
              <li key={cat.id} className='flex items-center gap-3 px-4 py-3'>
                <span
                  className='inline-block h-4 w-4 rounded-full flex-shrink-0'
                  style={{ backgroundColor: cat.color ?? '#94a3b8' }}
                />
                <span className='flex-1 text-sm font-medium text-slate-900'>{cat.name}</span>
                <button
                  onClick={() => startEdit(cat)}
                  className='rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition'
                  title='Modifier'
                >
                  <Edit className='h-3.5 w-3.5' />
                </button>
                <button
                  onClick={() => handleDelete(cat.id, cat.name)}
                  className='rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 transition'
                  title='Supprimer'
                >
                  <Trash2 className='h-3.5 w-3.5' />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}
